import * as React from "react";
import { Slot as SlotPrimitive } from "radix-ui";
import { cva, type VariantProps } from "class-variance-authority";

const chipVariants = cva(
  "group/chip inline-flex items-center gap-1.5 rounded-full border font-medium whitespace-nowrap transition-colors duration-fast outline-none focus-visible:ring-2 focus-visible:ring-ring/20 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      tone: {
        outline: "border-border bg-card text-foreground hover:border-action hover:text-action",
        soft: "border-border-subtle bg-card text-muted-foreground hover:border-border hover:text-foreground",
        selected: "border-action-fill bg-action-fill text-action-fill-foreground",
        picked: "border-action bg-action-tint text-foreground",
        dashed: "border-dashed border-border bg-transparent text-muted-foreground hover:border-action hover:text-action",
        warning: "border-transparent bg-warning-bg text-warning-strong hover:border-warning/40",
      },
      size: {
        default: "h-control-compact px-3 text-xs",
        lg: "h-control-sm px-4 text-sm pointer-coarse:h-11",
        touch: "h-11 px-4 text-sm",
      },
      focused: {
        true: "ring-2 ring-ring ring-offset-2 ring-offset-background",
      },
    },
    defaultVariants: { tone: "outline", size: "default", focused: false },
  },
);

export type ChipTone = NonNullable<VariantProps<typeof chipVariants>["tone"]>;
export type ChipSize = NonNullable<VariantProps<typeof chipVariants>["size"]>;

export interface ChipProps extends Omit<React.ComponentProps<"span">, "className" | "style"> {
  tone?: ChipTone;
  size?: ChipSize;
  asChild?: boolean;
  focused?: boolean;
}

function Chip({ tone = "outline", size = "default", asChild = false, focused = false, ...props }: ChipProps) {
  const Comp = asChild ? SlotPrimitive.Slot : "span";
  return <Comp {...props} data-slot="chip" data-tone={tone} className={chipVariants({ tone, size, focused })} />;
}

export type ChipCountProps = Omit<React.ComponentProps<"span">, "className" | "style">;

function ChipCount(props: ChipCountProps) {
  return <span {...props} data-slot="chip-count" className="rounded-full border border-border px-1.5 text-xs tabular-nums text-muted-foreground group-data-[tone=selected]/chip:border-action-fill-foreground/40 group-data-[tone=selected]/chip:text-action-fill-foreground" />;
}

export { Chip, ChipCount };
